import React, { useState, useContext, createContext } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import './styles/menu-item.css'

// Create context for menu state management
const MenuContext = createContext({
	openMenus: {},
	activeItem: null,
	toggleMenu: () => {},
	setActiveItem: () => {},
})

/**
 * Menu Provider component to manage global menu state
 */
export const MenuProvider = ({
	children,
	initialOpenMenus = {},
	initialActiveItem = null,
}) => {
	const [openMenus, setOpenMenus] = useState(initialOpenMenus)
	const [activeItem, setActiveItem] = useState(initialActiveItem)

	const toggleMenu = (menuId) => {
		setOpenMenus((prev) => ({
			...prev,
			[menuId]: !prev[menuId],
		}))
	}

	return (
		<MenuContext.Provider
			value={{ openMenus, activeItem, toggleMenu, setActiveItem }}
		>
			{children}
		</MenuContext.Provider>
	)
}

// Hook to use menu context
export const useMenu = () => useContext(MenuContext)

/**
 * @typedef {Object} SubMenuItem
 * @property {string} id - Unique identifier for the sub menu item
 * @property {string} title - Display text for the sub menu item
 * @property {string} path - Navigation path
 * @property {Function} [hideWhen] - Function that takes a user and returns boolean indicating if item should be hidden
 */

function MenuItems({
	id,
	title,
	icon,
	onClick = () => {},
	subMenu = null,
	active = false,
}) {
	const { openMenus, toggleMenu } = useMenu()
	const isSubMenuOpen = id ? openMenus[id] : false

	const handleClick = () => {
		if (subMenu) {
			if (id) {
				toggleMenu(id)
			}
		} else {
			onClick()
		}
	}

	return (
		<div className='menu-item-container'>
			<div
				className={`menu-item ${active ? 'active' : ''}`}
				onClick={handleClick}
			>
				{active && <div className='active-indicator' />}
				<div className='icon-container'>{icon}</div>
				<span className={`menu-title ${active ? 'active-text' : ''}`}>
					{title}
				</span>
				{subMenu && (
					<div className='submenu-indicator'>
						{isSubMenuOpen ? (
							<ChevronDown size={16} />
						) : (
							<ChevronRight size={16} />
						)}
					</div>
				)}
			</div>
			{subMenu && (
				<div
					className={`submenu-container ${isSubMenuOpen ? 'open' : 'closed'}`}
				>
					{subMenu}
				</div>
			)}
		</div>
	)
}

/**
 * Individual SubMenu Item component
 * @param {Object} props
 * @param {SubMenuItem} props.item
 * @param {boolean} props.isActive
 * @param {Function} props.onClick
 */
export const SubMenuItemComponent = ({ item, isActive, onClick }) => {
	const { setActiveItem } = useMenu()

	const handleClick = () => {
		setActiveItem(item.id)
		onClick(item.path)
	}

	return (
		<div
			className={`submenu-item ${isActive ? 'active' : ''}`}
			onClick={handleClick}
		>
			<span className='submenu-title'>• {item.title}</span>
		</div>
	)
}

export default MenuItems
